import { type SettingsResponse, algoliasearch } from 'algoliasearch';

import env from '~/env';

export const FACET_SEPARATOR = ' > ';

export const algoliaSearchClient = algoliasearch(
  env.ALGOLIA_APP_ID,
  env.ALGOLIA_API_KEY,
);

export const SORT_VALUES = [
  'relevance',
  'price_desc',
  'price_asc',
  'release_date_asc',
  'release_date_desc',
] as const;

export type SortValue = (typeof SORT_VALUES)[number];

export function getAlgoliaIndex({
  sort,
  language,
}: {
  sort?: SortValue;
  language: string;
}) {
  const baseIndex = [
    'webshop_products',
    language,
    env.ALGOLIA_INDEX_SUFFIX,
  ].join('_');

  switch (sort) {
    case 'price_asc':
      return `${baseIndex}_sort_price_asc`;
    case 'price_desc':
      return `${baseIndex}_sort_price_desc`;
    case 'release_date_desc':
      return `${baseIndex}_sort_release_date_desc`;
    case 'release_date_asc':
      return `${baseIndex}_sort_release_date_asc`;
    default:
      return baseIndex;
  }
}

export async function getAlgoliaWebshopUserData(
  language: string,
): Promise<AlgoliaWebshopUserData> {
  const algoliaIndex = getAlgoliaIndex({ language });
  const response = await algoliaSearchClient.getSettings({
    indexName: algoliaIndex,
  });

  const { userData, ranking } = response;

  if (!userData) return { ranking };

  // @TODO: very lazy validation maybe zod is better?

  const countryStoreMapping =
    'country_store_mapping' in userData &&
    typeof userData.country_store_mapping === 'object'
      ? (userData.country_store_mapping as Record<string, string>)
      : undefined;

  const slugMapping =
    'slug_mapping' in userData && typeof userData.slug_mapping === 'object'
      ? (userData.slug_mapping as Record<string, string>)
      : undefined;

  const slugHierarchyFull =
    'slug_hierarchy_full' in userData &&
    typeof userData.slug_hierarchy_full === 'object'
      ? (userData.slug_hierarchy_full as Record<string, string[]>)
      : undefined;

  const redirects =
    'redirects' in userData && typeof userData.redirects === 'object'
      ? (userData.redirects as Record<string, string>)
      : undefined;

  return {
    countryStoreMapping,
    slugMapping,
    slugHierarchyFull,
    redirects,
    ranking,
  };
}

export type AlgoliaWebshopUserData = {
  countryStoreMapping?: Record<string, string> | undefined;
  slugMapping?: Record<string, string> | undefined;
  slugHierarchyFull?: Record<string, string[]> | undefined;
  redirects?: Record<string, string> | undefined;
  ranking?: SettingsResponse['ranking'];
};
